// Light / dark theme. The choice ("system", "light" or "dark") is kept
// per browser in localStorage; "system" follows prefers-color-scheme
// and updates when it changes. Tailwind reads the ``dark`` class on
// <html>.

import { create } from "zustand";

export type ThemeChoice = "system" | "light" | "dark";

const KEY = "sheafworks.theme";
const query =
  typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(prefers-color-scheme: dark)")
    : null;

function stored(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
}

function apply(choice: ThemeChoice): void {
  const dark =
    choice === "dark" || (choice === "system" && (query?.matches ?? false));
  document.documentElement.classList.toggle("dark", dark);
}

interface ThemeState {
  choice: ThemeChoice;
  setChoice: (choice: ThemeChoice) => void;
}

export const useTheme = create<ThemeState>((set) => ({
  choice: stored(),
  setChoice: (choice) => {
    try {
      if (choice === "system") localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, choice);
    } catch {
      // Storage blocked: the choice still holds for this page.
    }
    apply(choice);
    set({ choice });
  },
}));

apply(useTheme.getState().choice);
query?.addEventListener("change", () => apply(useTheme.getState().choice));
